/**
 * Targeting (shared)
 * ──────────────────
 * Shared hit detection for weapons and interactions.
 *
 * Aim shapes (use.aim.shape) — always shown as a visual when aiming:
 *   'trap'         — trapezoid in front of caster; nearDist/nearWidth/farDist/farWidth
 *   'circle_self'  — circle centred on caster; radius
 *   'circle_aimed' — placeable circle; radius/minRange/maxRange
 *
 * Delivery:
 *   use.immediate   — instant hit within the aim shape; { damage: [min, max] | heal: [min, max], maxTargets, impactSprite? }
 *   use.projectiles — projectiles spawned on use; each owns { damage: [min, max] | heal: [min, max], flightSprite, impactSprite,
 *                     radius, speed, maxDistance, maxTargets, offsetDegrees, delay }
 *                     arc projectiles also have { arc, splashRadius } and bypass in-flight collision
 *
 * Every hittable entity must implement:
 *   isHitBy(item)  — return true if this entity can be hit by the given item
 *
 * Client: queryUseTargets — live targeting preview + target list (immediate hits only)
 * Server: queryShapeHits  — authoritative hit detection for immediate hits
 */

export const DEFAULT_AIM = {
  shape: 'trap',
  nearDist: 0.2,
  nearWidth: 1.1,
  farDist: 1.3,
  farWidth: 1.3,
}


const DIR_VECTORS = {
  north: { x: 0, y: 1 },
  south: { x: 0, y: -1 },
  east: { x: 1, y: 0 },
  west: { x: -1, y: 0 },
}

function resolveDir(direction) {
  if (typeof direction === 'string') return DIR_VECTORS[direction] || DIR_VECTORS.south
  return direction
}

/**
 * Query for all immediate-hit targets in range (client-side preview).
 * Only applies to weapons with use.immediate — projectile hits are resolved by ServerProjectiles.
 *
 * @returns {Entity[]}
 */
export function queryUseTargets({ x, y, direction, attack, candidates, item, isBlocked }) {
  if (!attack?.immediate) return []
  const t = attack.aim || {}
  const shape = t.shape || 'trap'
  const filter = entity => entity.isHitBy?.(item) ?? false
  const maxTargets = attack.immediate.maxTargets ?? 1

  if (shape === 'trap') {
    return queryTrap({ x, y, direction, targeting: { ...t, maxTargets }, candidates, isBlocked, filter }).targets
  }
  if (shape === 'circle_self') {
    return queryAoeSelf({ x, y, targeting: { ...t, maxTargets }, candidates, isBlocked, filter }).targets
  }
  return []
}

/**
 * Query for the closest interact-slot interactable (containers, corpses, etc).
 * These are entities that declare getInteractInfo() — activated by the interact key (E/RMB).
 *
 * @returns {{ target, distance }|null}
 */
export function queryInteract(opts) {
  return queryEntityAction({ ...opts, infoMethod: 'getInteractInfo' })
}

// ── Internal: single entity action query (for interact) ──

function queryEntityAction({ x, y, radius, direction, candidates, isBlocked, checkAction, infoMethod }) {
  const d = resolveDir(direction)

  let best = null
  let bestScore = -Infinity
  let bestDist = Infinity

  for (const entry of candidates) {
    const entity = entry.value
    const info = entity[infoMethod]?.()
    if (!info) continue

    const dx = entry.x - x
    const dy = entry.y - y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const effectiveDist = Math.max(0, dist - entry.radius)

    if (effectiveDist > radius) continue
    if (!checkAction(entity)) continue

    let dot = 1.0
    if (dist > 0.001) {
      dot = (dx / dist) * d.x + (dy / dist) * d.y
    }

    if (isBlocked?.(x, y, entry.x, entry.y, entity)) continue

    const score = dot / (effectiveDist + 0.01)
    if (score > bestScore) {
      bestScore = score
      best = entity
      bestDist = effectiveDist
    }
  }

  return best ? { target: best, distance: bestDist } : null
}

// ── Internal: trapezoid query ──

function queryTrap({ x, y, direction, targeting, candidates, isBlocked, filter }) {
  const d = resolveDir(direction)
  const perp = { x: -d.y, y: d.x }

  const nearDist = targeting.nearDist ?? -0.2
  const farDist = targeting.farDist ?? 0.8
  const nearWidth = targeting.nearWidth ?? 0.4
  const farWidth = targeting.farWidth ?? 1.2
  const maxTargets = targeting.maxTargets ?? 1
  const depthRange = farDist - nearDist

  const scored = []

  for (const entry of candidates) {
    const entity = entry.value
    if (filter && !filter(entity)) continue

    const dx = entry.x - x
    const dy = entry.y - y

    const depth = dx * d.x + dy * d.y
    const lateral = dx * perp.x + dy * perp.y

    const radius = getTargetRadius(entity)

    if (depth + radius < nearDist) continue
    if (depth - radius > farDist) continue

    const clampedDepth = Math.max(nearDist, Math.min(farDist, depth))
    const t = depthRange > 0.001 ? (clampedDepth - nearDist) / depthRange : 0.5
    const widthAtDepth = nearWidth + (farWidth - nearWidth) * t
    const halfWidth = widthAtDepth / 2

    if (Math.abs(lateral) - radius > halfWidth) continue

    if (isBlocked?.(x, y, entry.x, entry.y, entity)) continue

    const dist = Math.sqrt(dx * dx + dy * dy)
    const effectiveDist = Math.max(0, dist - radius)

    scored.push({ entity, effectiveDist })
  }

  scored.sort((a, b) => a.effectiveDist - b.effectiveDist)
  const results = scored.slice(0, maxTargets)

  return {
    targets: results.map(r => r.entity),
    distances: results.map(r => r.effectiveDist),
  }
}

// ── Internal: aoe around self ──

function queryAoeSelf({ x, y, targeting, candidates, isBlocked, filter }) {
  const range = targeting.radius || 1.5
  const maxTargets = targeting.maxTargets ?? Infinity

  const scored = []

  for (const entry of candidates) {
    const entity = entry.value
    if (filter && !filter(entity)) continue

    const dx = entry.x - x
    const dy = entry.y - y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const radius = getTargetRadius(entity)
    const effectiveDist = Math.max(0, dist - radius)
    if (effectiveDist > range) continue

    if (isBlocked?.(x, y, entry.x, entry.y, entity)) continue

    scored.push({ entity, effectiveDist })
  }

  scored.sort((a, b) => a.effectiveDist - b.effectiveDist)
  const results = scored.slice(0, maxTargets)

  return {
    targets: results.map(r => r.entity),
    distances: results.map(r => r.effectiveDist),
  }
}

function getTargetRadius(entity) {
  return entity.getTargetInfo?.()?.radius || 0
}

// ── Server-side shape hit detection ──

/**
 * Given an attacker position, direction, and weapon, find all entities hit immediately.
 * Only applies to use.immediate — projectile hits are handled by ServerProjectiles.
 * Returns [{ entity, delay }]
 */
export function queryShapeHits({ px, py, dx, dy, tx, ty, attack, item, attacker, candidates, isBlocked }) {
  if (!attack?.immediate) return []
  const t = attack.aim || {}
  const shape = t.shape || 'trap'
  const filter = entity => entity.isHitBy?.(item, attacker) ?? false
  const maxTargets = attack.immediate.maxTargets ?? 1

  if (shape === 'trap') {
    const direction = { x: dx, y: dy }
    const result = queryTrap({
      x: px,
      y: py,
      direction,
      targeting: { ...t, maxTargets },
      candidates,
      isBlocked,
      filter,
    })
    return result.targets.map(entity => ({ entity, delay: 0 }))
  }

  if (shape === 'circle_self') {
    const result = queryAoeSelf({ x: px, y: py, targeting: { ...t, maxTargets }, candidates, isBlocked, filter })
    return result.targets.map(entity => ({ entity, delay: 0 }))
  }

  if (shape === 'circle_aimed') {
    const cx = tx ?? px
    const cy = ty ?? py
    const radius = t.radius || 1.5
    const results = []
    for (const entry of candidates) {
      if (results.length >= maxTargets) break
      const entity = entry.value
      if (!filter(entity)) continue
      const hbRadius = entity.getTargetInfo?.()?.radius || 0
      const ddx = entry.x - cx
      const ddy = entry.y - cy
      const dist = Math.sqrt(ddx * ddx + ddy * ddy)
      if (dist > radius + hbRadius) continue
      if (isBlocked?.(px, py, entry.x, entry.y, entity)) continue
      results.push({ entity, delay: 0 })
    }
    return results
  }

  return []
}
